import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import Api, { DEFAULT_ERROR_MESSAGE } from '../../../../api/Api';
import { GLOBAL_HD_DTU_ID } from '../../../../utils/common';
import {
  EVENT_ANALYTICS_TYPE_UPDATED,
  trackUpdate,
} from '../../../../utils/matomoEvents';
import { BASIC_ANALYTICS } from '../../../../types/analytics';

interface TSPlotLogicProps {
  coordinates: number[];
  timeStepInterval: string;
  pointLabel: string;
  isFwe?: boolean;
  fwePointName?: string;
  setTimeSeriesPreview: (data: any) => void;
}

export const useTimeSeriesPlot = ({
  coordinates,
  timeStepInterval,
  pointLabel,
  isFwe,
  fwePointName,
  setTimeSeriesPreview,
}: TSPlotLogicProps) => {
  const [updatedTSData, setUpdatedTSData] = useState([]);
  const [errorTs, setErrorTs] = useState('');
  const pointName = isFwe ? fwePointName ?? pointLabel : pointLabel;

  const abortControllerRef = useRef<AbortController | null>(null);

  const clearTsData = () => {
    setUpdatedTSData([]);
    setTimeSeriesPreview((prev: any) => {
      if (!prev) return prev;
      return {
        ...prev,
        Data: {
          ...prev.Data,
          Items: null,
        },
      };
    });
  };

  const timeSeriesMutation = useMutation({
    mutationFn: async (values: any & { signal?: AbortSignal }) => {
      const {
        orderId,
        datasetName,
        startDate,
        endDate,
        parameters,
        month,
        signal,
      } = values;

      const data = await Api.chart.getBasicChartV2(
        orderId || '',
        pointName,
        datasetName,
        startDate,
        endDate,
        coordinates.join(),
        datasetName === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : 60,
        parameters,
        month,
        { signal }
      );

      return { data, datasetName };
    },
    onSuccess: ({ data, datasetName }) => {
      if (data?.Data?.Items) {
        setUpdatedTSData(data.Data.Items);
        trackUpdate(
          EVENT_ANALYTICS_TYPE_UPDATED,
          coordinates,
          datasetName,
          BASIC_ANALYTICS.TIME_SERIES.toLowerCase()
        );
      } else {
        clearTsData();
      }
      setErrorTs('');
    },
    onError: (error: any) => {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        clearTsData();
        return;
      }
      setErrorTs(error?.response?.data?.Message || DEFAULT_ERROR_MESSAGE);
      clearTsData();
    },
  });

  const onUpdateTS = useCallback(
    async (values: any) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      timeSeriesMutation.mutate({
        ...values,
        signal: controller.signal,
      });
    },
    [timeSeriesMutation]
  );

  const resetTSPlot = () => {
    setUpdatedTSData([]);
    setErrorTs('');
  };

  const cancelUpdateTS = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  return {
    updatedTSData,
    errorTs,
    onUpdateTS,
    resetTSPlot,
    cancelUpdateTS,
    loadingUpdateTS: timeSeriesMutation.isPending,
  };
};
